
import { toast as sonnerToast } from 'sonner';

// Tipos de toast padronizados
export enum ToastType {
  SUCCESS = 'success',
  ERROR = 'error', 
  INFO = 'info',
  WARNING = 'warning'
}

// Configurações padrão
const DEFAULT_DURATION = {
  success: 4000,
  error: 6000,
  info: 4000,
  warning: 5000
};

// Mensagens padronizadas
export const TOAST_MESSAGES = {
  auth: {
    login: {
      success: 'Login realizado com sucesso',
      error: 'Erro ao fazer login'
    },
    logout: {
      success: 'Você saiu com sucesso',
      error: 'Erro ao sair'
    },
    sessionExpired: 'Sua sessão expirou. Por favor, faça login novamente.'
  },
  connection: {
    restored: 'Conexão restaurada',
    lost: 'Conexão perdida',
    retrying: 'Tentando reconectar...'
  }
} as const;

// Cache para evitar toasts duplicados
const toastCache = new Map<string, number>();
const DEBOUNCE_TIME = 3000; // 3 segundos

class ToastService {
  private static instance: ToastService;

  private constructor() {}

  static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService();
    }
    return ToastService.instance;
  }

  private shouldShowToast(key: string): boolean {
    const now = Date.now();
    const lastShown = toastCache.get(key);
    
    if (!lastShown || now - lastShown > DEBOUNCE_TIME) {
      toastCache.set(key, now);
      return true;
    }
    
    return false;
  }

  private showToast(type: ToastType, message: string, description?: string, key?: string) {
    const toastKey = key || `${type}-${message}`;
    
    if (!this.shouldShowToast(toastKey)) {
      return;
    }

    const options = {
      duration: DEFAULT_DURATION[type],
      closeButton: true,
      position: 'bottom-right' as const
    };

    switch (type) {
      case ToastType.SUCCESS:
        return sonnerToast.success(message, {
          description,
          ...options
        });
      case ToastType.ERROR:
        return sonnerToast.error(message, {
          description,
          ...options
        });
      case ToastType.INFO:
        return sonnerToast.info(message, {
          description,
          ...options
        });
      case ToastType.WARNING:
        return sonnerToast.warning(message, {
          description,
          ...options
        });
    }
  }

  success(message: string, description?: string, key?: string) {
    return this.showToast(ToastType.SUCCESS, message, description, key);
  }

  error(message: string, description?: string, key?: string) {
    return this.showToast(ToastType.ERROR, message, description, key);
  }

  info(message: string, description?: string, key?: string) {
    return this.showToast(ToastType.INFO, message, description, key);
  }

  warning(message: string, description?: string, key?: string) {
    return this.showToast(ToastType.WARNING, message, description, key);
  }

  // Methods for specific use cases
  authLoginSuccess() {
    this.success(TOAST_MESSAGES.auth.login.success, undefined, 'auth-login-success');
  }

  authLoginError(error?: string) {
    this.error(TOAST_MESSAGES.auth.login.error, error, 'auth-login-error');
  }

  authLogoutSuccess() {
    this.success(TOAST_MESSAGES.auth.logout.success, undefined, 'auth-logout-success');
  }

  authLogoutError() {
    this.error(TOAST_MESSAGES.auth.logout.error, undefined, 'auth-logout-error');
  }

  authSessionExpired() {
    this.error(TOAST_MESSAGES.auth.sessionExpired, undefined, 'auth-session-expired');
  }

  connectionRestored() {
    this.success(TOAST_MESSAGES.connection.restored, undefined, 'connection-restored');
  }

  connectionLost() {
    this.warning(TOAST_MESSAGES.connection.lost, 'Algumas funcionalidades podem ficar limitadas', 'connection-lost');
  }

  connectionRetrying() {
    this.info(TOAST_MESSAGES.connection.retrying, undefined, 'connection-retrying');
  }

  // Clear cache method for testing
  clearCache() {
    toastCache.clear();
  }
}

export const toastService = ToastService.getInstance();
